import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import Stepper from '@mui/material/Stepper';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
    DataGridPro,
    GridColDef,
    useGridApiContext,
    GridRowParams,
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    useGridSelector,
    gridDimensionsSelector,
} from '@mui/x-data-grid-pro';
import {Card, Input, StepLabel} from "@mui/material";
import Grid from '@mui/material/Grid';
import {useChatbotContext} from "../../context";
import {useEffect, useState} from "react";
import Button from "@mui/material/Button";
import {useDocumentStatusAPI} from "../as2-onboarding-component/hooks/useClient";
import {useOktaAuth} from "@okta/okta-react";
import CircularProgress from "@mui/material/CircularProgress";
import {generateUniqueID} from "web-vitals/dist/modules/lib/generateUniqueID";
import {AxiosInstance} from "axios";

interface EDIDocumentSimplified {
    ID: number,
    STATUS: 'Success' | 'Error' | 'Processing' | string;
    TRANSACTION_TYPE: string; // e.g., "undefined - undefined"
    ENVELOPE_NAME: string;
    REF_TYPE: string;
    REF_VALUE: string;
    CREATE_DATE: Date; // ISO 8601 format
    LAST_UPDATE_DATE: Date; // ISO 8601 format
    SUPPLIER_ID: string;
    APP_FILE_ARCHIVE: string | null;
    EDI_FILE_ARCHIVE: string | null;
    APPL_MAILBOX: string;
    PO: string;
    PART_NBR: string;
    TRACKING_NUM: string;
    BATCH_ID: string | null;
    REF_NUM: string; //EDI_ARCHIVE_KEY
    SAP_STATUS: string; // e.g., "53"
}

interface SearchFormProps {
    onSearch: (form: EdiDetailsForm | SapDetailsForm) => void;
}

interface EdiDetailsForm {
    transaction: string;
    supplier: string;
    reference_value: string;
    unit: string;
}

interface SapDetailsForm {
    ediArchiveKey: string;
}

interface DocumentPanelValues {
    idoc: string;
    steps: HistoricalStep[];
    createdAt: string;
    lastUpdated: string;
    ediArchiveKey: string;
    activeStep: number;
}


interface HistoricalResponse {
    ediDocument: EDIDocumentDetailed;
    sapDocument: SAPDocument;
}

interface EDIDocumentDetailed {
    id: number;
    envelope: string;
    ref_value: string;
    archive_key: string;
    status: Status;
    createdAt: string;
    updatedAt: string;
}

interface SAPDocument {
    idoc: string; //Docnum
    ediArchiveKey: string; //Arckey
    currentStatus: Status;
    idocType: string; //Idoctp
    ediType: string; //Stdmes
    createdAt: string;
    updatedAt: string;
    direction: string //Direct
    documentHistory: SAPDocumentHistory[];
}

interface SAPDocumentHistory{
    idoc: string;
    createdAt: string;
    status: Status;
    statusExtraInfo: string; //Statustxt
}

interface Status {
    statusSummary: 'Error' | 'Success' | 'Processing';
    statusID: number;
    statusDescription: string;
}

interface HistoricalStep{
    id: string;
    title: string;
    status: string;
    statusExtraInfo: string;
    description: string;
    timestamp: string;
}

async function getDocumentHistory(document: EDIDocumentSimplified, documentStatusClient: AxiosInstance): Promise<DocumentPanelValues> {
    const ediArchiveKey = document.REF_NUM;
    const response = await documentStatusClient.get<HistoricalResponse>(`/document-status/sap-history?archive_key=${ediArchiveKey}`);
    const historicalData: HistoricalResponse = response.data;

    const steps: HistoricalStep[] = [];

    // Step 1: EDI - Reception
    steps.push({
        id: generateUniqueID(),
        title: 'EDI - Reception Result',
        status: 'Success',
        statusExtraInfo: 'N/A',
        description: 'N/A - Document was received by EDI system.',
        timestamp: historicalData.ediDocument.createdAt,
    });

    // Step 2: EDI - Processing
    steps.push({
        id: generateUniqueID(),
        title: 'EDI - Processing Result',
        status: historicalData.ediDocument.status.statusSummary,
        statusExtraInfo: 'N/A',
        description: `${historicalData.ediDocument.status.statusID} - ${historicalData.ediDocument.status.statusDescription}`,
        timestamp: historicalData.ediDocument.updatedAt,
    });

    // Step 3: SAP - Reception
    const sapReception = historicalData.sapDocument?.documentHistory?.[0];
    steps.push({
        id: generateUniqueID(),
        title: 'SAP - Reception Result',
        status: sapReception?.status.statusSummary ?? 'Pending',
        statusExtraInfo: sapReception?.statusExtraInfo ?? 'N/A',
        description: sapReception
            ? `${sapReception.status.statusID} - ${sapReception.status.statusDescription}`
            : 'N/A - No SAP reception data available.',
        timestamp: sapReception ? sapReception.createdAt : 'N/A',
    });

    // Step 4: SAP - Processing response - Only if is different than the first element
    const sapHistory = historicalData.sapDocument?.documentHistory ?? [];
    const sapProcessing = sapHistory.length > 1 ? sapHistory[sapHistory.length - 1] : null;
    steps.push({
        id: generateUniqueID(),
        title: 'SAP - Processing Result',
        status: sapProcessing?.status.statusSummary ?? 'Pending',
        statusExtraInfo: sapProcessing?.statusExtraInfo ?? '',
        description: sapProcessing
            ? `${sapProcessing.status.statusID} - ${sapProcessing.status.statusDescription}`
            : 'N/A - No SAP processing data available.',
        timestamp: sapProcessing ? sapProcessing.createdAt : 'N/A',
    });

    const sapHistoryLength = historicalData.sapDocument?.documentHistory?.length ?? 0;

    let activeStep = 1;
    if (sapHistoryLength > 1) {
        // SAP has finished
        activeStep = 4;
    } else if (sapHistoryLength === 1) {
        // SAP has started but not finished
        activeStep = 2;
    } else {
        // Only EDI processed the file
        activeStep = 2;
    }

    return {
        idoc: historicalData.sapDocument?.idoc ?? 'N/A',
        ediArchiveKey: historicalData.ediDocument.archive_key,
        steps,
        createdAt: historicalData.ediDocument.createdAt,
        lastUpdated: historicalData.sapDocument?.updatedAt ?? 'N/A',
        activeStep
    };
}

function DetailPanelContent({ row: rowProp }: { row: EDIDocumentSimplified }) {
    const apiRef = useGridApiContext();
    const width = useGridSelector(apiRef, gridDimensionsSelector).viewportInnerSize.width;
    const { oktaAuth } = useOktaAuth();
    const documentStatusClient = useDocumentStatusAPI(oktaAuth.getAccessToken());
    const [detailedDocumentValues, setDetailedDocumentValues] = useState<DocumentPanelValues>({
        idoc: '',
        steps: [],
        createdAt: '',
        lastUpdated: '',
        ediArchiveKey: '',
        activeStep: 0
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            setIsLoading(true);
            try {
                const detailedDocument: DocumentPanelValues = await getDocumentHistory(rowProp, documentStatusClient);
                if (isMounted) {
                    setDetailedDocumentValues(detailedDocument);
                }
            } catch (error) {
                console.error('Error loading SAP history:', error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [rowProp.REF_NUM]);

    return (
        <Stack
            sx={{
                py: 2,
                height: '100%',
                boxSizing: 'border-box',
                position: 'sticky',
                left: 0,
                width,
            }}
            direction="column"
        >
            <Paper sx={{ flex: 1, mx: 'auto', width: '90%', p: 2 }}>
                <Grid container spacing={2} py={4}>
                    <Grid size={4}>
                        <Box>
                            <Typography variant="h3" gutterBottom>IDOC#</Typography>
                            <Typography variant="bodyMedium" color="text.secondary">
                                {detailedDocumentValues.idoc}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={4}>
                        <Box>
                            <Typography variant="h3" gutterBottom>Created At</Typography>
                            <Typography variant="bodyMedium" color="text.secondary">
                                {detailedDocumentValues.createdAt}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={4}>
                        <Box>
                            <Typography variant="h3" gutterBottom>Last Updated</Typography>
                            <Typography variant="bodyMedium" color="text.secondary">
                                {detailedDocumentValues.lastUpdated}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Divider />

                <Grid container spacing={0.5} sx={{ mt: 4 }}>
                    <Grid size={3} sx={{ mt: 10 }}>
                        <Stepper activeStep={detailedDocumentValues.activeStep} orientation="vertical">
                            {detailedDocumentValues.steps.map((step, index) => (
                                <Step key={step.id}>
                                    <StepLabel>{step.title}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Grid>

                    <Grid size={9} sx={{ mb: 1 }}>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            <Grid size={2}><Typography variant="h3">Status</Typography></Grid>
                            <Grid size={4}><Typography variant="h3">Description</Typography></Grid>
                            <Grid size={3}><Typography variant="h3">Error Notes</Typography></Grid>
                            <Grid size={3}><Typography variant="h3">Date</Typography></Grid>
                        </Grid>

                        {detailedDocumentValues.steps.map((step) => (
                            <Grid container spacing={2} sx={{ mb: 5, py: 0.3 }} key={step.id} alignItems="center">
                                {/*Status Column*/}
                                <Grid size={2}>
                                    {step.status === 'Success' ? (
                                        <Chip
                                            color="success"
                                            icon={<fuel-icon name="check_circle" />}
                                            label="Success"
                                            variant="badge"
                                        />
                                    ) : step.status === 'Error' ? (
                                        <Chip
                                            color="error"
                                            icon={<fuel-icon name="error" />}
                                            label="Error"
                                            variant="badge"
                                        />
                                    ) : (
                                        <Chip
                                            color={'informational'}
                                            icon={<fuel-icon name={'info'}/>}
                                            label="Pending"
                                            variant="badge"
                                        />
                                    )}
                                </Grid>
                                {/*Description Column*/}
                                <Grid size={4}
                                      sx={{
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                      }}
                                >{step.description}</Grid>
                                {/*Error Notes Column*/}
                                <Grid size={3}
                                      sx={{
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                      }}
                                >{step.statusExtraInfo}</Grid>
                                {/*Date Date*/}
                                <Grid size={3}>{step.timestamp}</Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Paper>
        </Stack>
    );
}

function SearchForm({onSearch}: SearchFormProps) {
    const { selectedHistoricalParams } = useChatbotContext();
    const { openDrawer } = useChatbotContext();

    const [ediDetailsForm, setEdiDetailsForm] = useState<EdiDetailsForm>({
        transaction: '',
        supplier: '',
        reference_value: '',
        unit: '',
    });

    const [sapDetailsForm, setSapDetailsForm] = useState<SapDetailsForm>({
        ediArchiveKey: ''
    });

    const isEDIFormValid = ediDetailsForm.transaction && ediDetailsForm.supplier && ediDetailsForm.reference_value && ediDetailsForm.unit;
    const isSapFormValid = sapDetailsForm.ediArchiveKey;


    useEffect(() => {
        //Observer for context changes - if there are changes (click from chatbot), update the form and trigger search
        if (selectedHistoricalParams) {
            let archiveKeyFromContextObject = {
                ediArchiveKey: selectedHistoricalParams.REF_NUM
            };

            setSapDetailsForm(archiveKeyFromContextObject);
            // Trigger search with the new form data
            onSearch(archiveKeyFromContextObject);
        }
    }, [selectedHistoricalParams]);


    return (
        <>
            <Typography variant='h2'>EDI Details</Typography>
            <Typography variant='bodyMedium'>Use these fields if you know the EDI Details for a document</Typography>
            {/*EDI DETAILS FORM*/}
            <form onSubmit={(e) => {
                e.preventDefault();
                onSearch(ediDetailsForm);
                // lógica de envío
            }}>
                <Box display="flex" alignItems="flex-end" gap={5} pt={0}>
                    <Box flex={1}>
                        <Typography gutterBottom variant="h3">Transaction</Typography>
                        <Input id="transaction"
                               fullWidth
                               value={ediDetailsForm.transaction}
                               disabled={sapDetailsForm.ediArchiveKey !== ''}
                               onChange={(e) =>
                                   setEdiDetailsForm({...ediDetailsForm, transaction: e.target.value})
                               }
                        />
                    </Box>
                    <Box flex={2}>
                        <Typography gutterBottom variant="h3">Supplier</Typography>
                        <Input id="supplier"
                               fullWidth
                               value={ediDetailsForm.supplier}
                               disabled={sapDetailsForm.ediArchiveKey !== ''}
                               onChange={(e) =>
                                   setEdiDetailsForm({...ediDetailsForm, supplier: e.target.value})
                               }/>
                    </Box>
                    <Box flex={1}>
                        <Typography gutterBottom variant="h3">Reference Value</Typography>
                        <Input id="reference_value"
                               fullWidth
                               value={ediDetailsForm.reference_value}
                               disabled={sapDetailsForm.ediArchiveKey !== ''}
                               onChange={(e) =>
                                   setEdiDetailsForm({...ediDetailsForm, reference_value: e.target.value})
                               }/>
                    </Box>
                    <Box flex={1}>
                        <Typography gutterBottom variant="h3">Unit</Typography>
                        <Input id="unit"
                               fullWidth
                               value={ediDetailsForm.unit}
                               disabled={sapDetailsForm.ediArchiveKey !== ''}
                               onChange={(e) =>
                                   setEdiDetailsForm({...ediDetailsForm, unit: e.target.value})
                               }/>
                    </Box>
                    <Box>
                        <Button type="submit" variant="primary"
                                disabled={!isEDIFormValid}>
                            Search
                        </Button>
                    </Box>
                </Box>
            </form>
            {/*EDI DETAILS FORM - END */}

            <Divider sx={{my: 2}}>OR</Divider>
            <Typography variant='h2'>SAP Details</Typography>
            <Typography variant='bodyMedium'>Use these fields if you know the SAP Details for a document</Typography>
            {/*SAP DETAILS FORM*/}
            <form onSubmit={(e) => {
                e.preventDefault();
                onSearch(sapDetailsForm);
                // lógica de envío
            }}>
                <Box display="flex" alignItems="flex-end" gap={5} pt={0}>
                    <Box flex={1}>
                        <Typography gutterBottom variant="h3">EDI Archive Key</Typography>
                        <Input id="edi_archive_key"
                               fullWidth
                               disabled={ediDetailsForm.transaction !== '' || ediDetailsForm.supplier !== '' || ediDetailsForm.reference_value !== '' || ediDetailsForm.unit !== ''}
                               value={sapDetailsForm.ediArchiveKey}
                               onChange={(e) =>
                                   setSapDetailsForm({ ...sapDetailsForm, ediArchiveKey: e.target.value })
                               }
                        />
                    </Box>
                    <Box>
                        <Button type="submit" variant="primary"
                                disabled={!isSapFormValid}>
                            Search
                        </Button>
                    </Box>
                </Box>
            </form>

            {/*SAP DETAILS FORM - END */}

            <Divider sx={{my: 2}}>OR</Divider>

            <Box display="flex" justifyContent="center" sx={{mb: 5}}>
                <Button type="submit" variant="primary" endIcon={<AutoAwesomeIcon/>}
                    onClick={openDrawer}
                >
                    Find with AI
                </Button>
            </Box>
        </>
    );
}

const CustomLoadingOverlay = () => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "62vh",
            textAlign: "center",
        }}
    >
        <CircularProgress color="primary"/>
        <Typography color="grey" fontWeight="600" component="h2" variant="h1">
            Getting documents...
        </Typography>
    </Box>
)

export function DocumentHistoryMainView() {
    const [foundDocuments, setFoundDocuments] = useState<EDIDocumentSimplified[]>([]);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
    const columns: GridColDef[] = [
        {
            field: 'STATUS',
            headerName: 'Status',
            width: 200,
            renderCell: (params) => {
                const status = params.value?.toLowerCase();
                if (status === 'processed') {
                    return (
                        <Chip
                            color="success"
                            icon={<fuel-icon name="check_circle" />}
                            label="Processed"
                            variant="badge"
                        />
                    );
                }

                if (status === 'error') {
                    return (
                        <Chip
                            color="error"
                            icon={<fuel-icon name="error" />}
                            label="Error"
                            variant="badge"
                        />
                    );
                }

                return  <Chip
                    color="success"
                    icon={<fuel-icon name="check_circle" />}
                    label="Processed"
                    variant="badge"
                />
            }
        },
        { field: 'ENVELOPE_NAME', headerName: 'Envelope', width: 300 },
        { field: 'REF_VALUE', headerName: 'Reference Value', width: 300 },
        { field: 'REF_NUM', headerName: 'EDI Archive Key', width: 300 }
    ];
    const { oktaAuth } = useOktaAuth();
    const documentStatusClient = useDocumentStatusAPI(oktaAuth.getAccessToken());

    const getDetailPanelContent = React.useCallback(
        ({ row }: GridRowParams) => <DetailPanelContent row={row} />,
        [],
    );

    const getDetailPanelHeight = React.useCallback(() => 400, []);

    const searchDocuments = async (form: EdiDetailsForm | SapDetailsForm) => {
        try {
            setIsLoadingDocuments(true);
            const { reference_value, ediArchiveKey } = form as any;
            const key = reference_value || ediArchiveKey;
            const response = await documentStatusClient.get<EDIDocumentSimplified[]>(`/document-status/reference-num?ref_num=${key}`);
            const ediDocuments = response.data ? response.data : [];
            setFoundDocuments(ediDocuments);
            setIsLoadingDocuments(false);
        } catch (error) {
            console.error('Error fetching documents:', error);
        } finally {
            setIsLoadingDocuments(false);
        }
    };

    return (
        <Card sx={{p: 4}}>
            <Typography variant='h1'>Document History</Typography>
            <Divider sx={{my: 2}}/>
            <SearchForm onSearch={searchDocuments}/>
            <Box sx={{width: '100%', height: 800}}>
                <DataGridPro
                    columns={columns}
                    rows={foundDocuments}
                    initialState={{
                        pinnedColumns: {
                            left: [GRID_DETAIL_PANEL_TOGGLE_FIELD],
                        },
                    }}
                    getRowId={(row) => row.ID}
                    loading={isLoadingDocuments}
                    slots={{ loadingOverlay: CustomLoadingOverlay }}
                    getDetailPanelHeight={getDetailPanelHeight}
                    getDetailPanelContent={getDetailPanelContent}
                    sx={{
                        '& .MuiDataGrid-detailPanel': {
                            overflow: 'visible',
                        },
                    }}
                />
            </Box>
        </Card>
    );
}
